import React, { useState, useEffect } from 'react';
import { FileSignature, Users, Calendar, AlertCircle, CheckCircle, Clock, X } from 'lucide-react';

const ESignatureManager = ({ case_id, lead_id, user_id, onClose }) => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [pagination, setPagination] = useState({});

    useEffect(() => {
        fetchRequests();
    }, []);

    const fetchRequests = async (page = 1) => {
        try {
            setLoading(true);
            const response = await fetch(`https://api.casedb.co/esignature.php?page=${page}&per_page=25`, { credentials: 'include' });
            const data = await response.json();
            
            if (data.success) {
                setRequests(data.requests);
                setPagination(data.pagination);
            }
        } catch (error) {
            console.error('Error fetching signature requests:', error);
        } finally {
            setLoading(false);
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'completed':
                return <CheckCircle className="text-green-500" size={16} />;
            case 'partially_signed':
                return <Clock className="text-yellow-500" size={16} />;
            case 'sent':
                return <Clock className="text-blue-500" size={16} />;
            case 'expired':
                return <AlertCircle className="text-red-500" size={16} />;
            case 'cancelled':
                return <X className="text-gray-500" size={16} />;
            default:
                return <Clock className="text-gray-400" size={16} />;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed':
                return 'bg-green-100 text-green-800';
            case 'partially_signed':
                return 'bg-yellow-100 text-yellow-800';
            case 'sent':
                return 'bg-blue-100 text-blue-800';
            case 'expired':
                return 'bg-red-100 text-red-800';
            case 'cancelled':
                return 'bg-gray-100 text-gray-800';
            default:
                return 'bg-gray-100 text-gray-600';
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleCreateNew = () => {
        setShowCreateForm(true);
    };

    const handleRequestCreated = (newRequest) => {
        setRequests(prev => [newRequest, ...prev]);
        setShowCreateForm(false);
    };

    const handleViewRequest = (request) => {
        setSelectedRequest(request);
    };

    const handleCancelRequest = async (requestId) => {
        if (!window.confirm('Are you sure you want to cancel this signature request?')) {
            return;
        }

        try {
            const response = await fetch(`https://api.casedb.co/esignature.php?request_id=${requestId}`, {
                method: 'DELETE'
            });
            const data = await response.json();
            
            if (data.success) {
                fetchRequests(); // Refresh the list
            } else {
                alert('Failed to cancel request: ' + data.error);
            }
        } catch (error) {
            console.error('Error cancelling request:', error);
            alert('Failed to cancel request');
        }
    };

    if (showCreateForm) {
        return <ESignatureCreateForm 
            case_id={case_id} 
            lead_id={lead_id} 
            onCancel={() => setShowCreateForm(false)}
            onSuccess={handleRequestCreated}
        />;
    }

    if (selectedRequest) {
        return <ESignatureDetails 
            request={selectedRequest}
            onBack={() => setSelectedRequest(null)}
            onRefresh={fetchRequests}
        />;
    }

    return (
        <div className="page-container esignature-manager">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-2xl font-bold flex items-center gap-2">
                        <FileSignature size={24} />
                        eSignature Requests
                    </h2>
                    <p className="text-gray-600">Manage document signature requests</p>
                </div>
                <div className="flex gap-2">
                    <button 
                        onClick={handleCreateNew}
                        className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 flex items-center gap-2"
                    >
                        <FileSignature size={16} />
                        New Request
                    </button>
                    {onClose && (
                        <button 
                            onClick={onClose}
                            className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                        >
                            Close
                        </button>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center items-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                </div>
            ) : (
                <>
                    <div className="grid gap-4">
                        {requests.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                                <FileSignature size={48} className="mx-auto mb-4 opacity-30" />
                                <p>No signature requests found</p>
                                <button 
                                    onClick={handleCreateNew}
                                    className="mt-4 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                                >
                                    Create Your First Request
                                </button>
                            </div>
                        ) : (
                            requests.map(request => (
                                <div key={request.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                                    <div className="flex justify-between items-start">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-2">
                                                <h3 className="font-semibold text-lg">{request.request_title}</h3>
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                                                    {request.status.replace('_', ' ')}
                                                </span>
                                            </div>
                                            <p className="text-gray-600 mb-2">{request.document_name}</p>
                                            <div className="flex items-center gap-4 text-sm text-gray-500">
                                                <span className="flex items-center gap-1">
                                                    <Users size={14} />
                                                    {request.signed_count}/{request.total_signers} signed
                                                </span>
                                                <span className="flex items-center gap-1">
                                                    <Calendar size={14} />
                                                    Created {formatDate(request.created_at)}
                                                </span>
                                                {request.expires_at && (
                                                    <span className="flex items-center gap-1">
                                                        <Clock size={14} />
                                                        Expires {formatDate(request.expires_at)}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 ml-4">
                                            {getStatusIcon(request.status)}
                                            <button 
                                                onClick={() => handleViewRequest(request)}
                                                className="bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded text-sm"
                                            >
                                                View
                                            </button>
                                            {['draft', 'sent', 'partially_signed'].includes(request.status) && (
                                                <button 
                                                    onClick={() => handleCancelRequest(request.id)}
                                                    className="bg-red-100 hover:bg-red-200 text-red-600 px-3 py-1 rounded text-sm"
                                                >
                                                    Cancel
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {pagination.total_pages > 1 && (
                        <div className="flex justify-center mt-6">
                            <div className="flex gap-2">
                                {Array.from({ length: pagination.total_pages }, (_, i) => i + 1).map(page => (
                                    <button
                                        key={page}
                                        onClick={() => fetchRequests(page)}
                                        className={`px-3 py-1 rounded ${
                                            page === pagination.current_page
                                                ? 'bg-blue-500 text-white'
                                                : 'bg-gray-100 hover:bg-gray-200'
                                        }`}
                                    >
                                        {page}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

// Export placeholder components that will be implemented separately
const ESignatureCreateForm = ({ case_id, lead_id, onCancel, onSuccess }) => {
    return (
        <div className="page-container bg-white p-6 rounded-lg">
            <h3 className="text-xl font-bold mb-4">Create Signature Request</h3>
            <p className="text-gray-600">Create form component will be implemented here...</p>
            <div className="flex gap-2 mt-4">
                <button onClick={onCancel} className="bg-gray-500 text-white px-4 py-2 rounded">
                    Cancel
                </button>
            </div>
        </div>
    );
};

const ESignatureDetails = ({ request, onBack, onRefresh }) => {
    return (
        <div className="page-container bg-white p-6 rounded-lg">
            <h3 className="text-xl font-bold mb-4">Signature Request Details</h3>
            <p className="text-gray-600">Details component will be implemented here...</p>
            <div className="flex gap-2 mt-4">
                <button onClick={onBack} className="bg-gray-500 text-white px-4 py-2 rounded">
                    Back
                </button>
            </div>
        </div>
    );
};

export default ESignatureManager;
